import React from 'react';
import {Button, type ButtonProps} from '@mui/material';
import {useLocation, useNavigate} from 'react-router';
import {COLORS} from "~/style/colors-ts";
import {Stack, type SxProps} from "@mui/system";

export type BtnGroupTabItem = {
    title: string;
    Icon?: React.ElementType;
    /** Relative segment appended to the current path when clicked. */
    route?: string;
    onClick?: () => void;
};

export type BtnGroupTabsProps = {
    items: BtnGroupTabItem[];
    /** Defaults to `inherit`. */
    color?: ButtonProps['color'];
    size?: ButtonProps['size'];
    className?: string;
    /**
     * Selected key for items without a `route` (matched against `title`).
     * Route items are selected from the current location instead.
     */
    value?: string;
    sx?: SxProps;
};

function joinPath(base: string, segment: string): string {
    const b = base.replace(/\/$/, '');
    const s = segment.replace(/^\//, '');
    return `${b}/${s}`;
}

/** If the pathname already ends with a tab route, use the parent as the base. */
function getBasePath(pathname: string, routes: string[]): string {
    for (const route of routes) {
        const normalized = route.replace(/^\/+|\/+$/g, '');
        if (!normalized) continue;
        const suffix = `/${normalized}`;
        if (pathname === suffix || pathname.endsWith(suffix)) {
            return pathname.slice(0, -suffix.length) || '/';
        }
    }
    return pathname.replace(/\/$/, '') || '/';
}

export function BtnGroupTabs({
                                 items,
                                 color = 'inherit',
                                 size,
                                 className,
                                 value,
                                 sx,
                             }: BtnGroupTabsProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const itemRoutes = items.flatMap(item => (item.route != null ? [item.route] : []));
    const basePath = getBasePath(location.pathname, itemRoutes);

    return (
        <Stack
            className={className}
            gap={0.5}
            direction="row"
            sx={sx}
        >
            {items.map(item => {
                const targetPath = item.route != null ? joinPath(basePath, item.route) : undefined;
                const selected = targetPath != null
                    ? location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`)
                    : value != null && value === item.title;

                const Icon = item.Icon;

                return (
                    <Button
                        key={item.route ?? item.title}
                        color={color}
                        size={size}
                        variant={selected ? 'contained' : 'text'}
                        startIcon={Icon ? <Icon style={{width: 18, height: 18}} /> : undefined}
                        onClick={() => {
                            if (item.route != null) {
                                navigate(joinPath(basePath, item.route));
                            }
                            item.onClick?.();
                        }}
                        sx={{px: '10px', ontWeight: 500, fontSize: '13px', ...(color === 'inherit' ? {color: !selected ? COLORS.grey[600] : 'inherit'} : {})}}
                    >
                        {item.title}
                    </Button>
                );
            })}
        </Stack>
    );
}
